// STRIKZA — SLIP REPAIR · page shell.
//
// SR-WIRE (this slice): the page now drives the live backend.
//   • Reads `?session=<id>` from the URL via useSearchParams.
//   • Absent session → UploadScreen (live). On successful upload the
//     page navigates to ?session=<id> and advances to Configure.
//   • Configure → POST /slip-repair-run with the chosen mode, then
//     polls GET /slip-repair-api/session/:id every 1.5s until
//     `repaired_at` is non-null, then advances to Results.
//   • Results render from the live session+legs (mapped into the
//     existing stub `Leg` shape so the UI components keep working).
//   • LegCard ACCEPT / OVERRIDE / REMOVE → PATCH /leg/:id with
//     refetch; in-flight + per-leg errors surfaced inline.
//
// DEV-mode stub fallback: in `import.meta.env.DEV`, when no
// `?session=` param is present, a toggle lets a developer view the
// page rendered from `_stubData.ts` without burning real backend
// calls. Production builds always go through the live flow.
//
// REDACTIONS (per STR-PROD-008 v2.1.1, preserved):
//   R1 — no agent names rendered.
//   R2 — magnitude deltas behind WhyDisclosure.
//   R3 — UNRESOLVED renders the split-view (covered vs missing).
//   R4 — RepairingScreen stays two-phase (no agent-dispatch theatre).
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useSearchParams } from "react-router-dom";

import { PaywallCard } from "../../components/PaywallCard.tsx";
import { useUserTier } from "../../auth/useUserTier.ts";

import { SLIP_LEGS, SLIP_META, tierToLegacyMode } from "./_stubData.ts";
import type { Leg, RepairMode, RepairTier, SlipMeta, TierPicks, VoidLevel } from "./_stubData.ts";
import { createShareLink, exportPdf, patchLeg, runRepair } from "./repairApi.ts";
import { buildRepairPdfModel } from "../../repair/buildRepairPdfModel.ts";
import { downloadRepairPdf } from "../../repair/repairSharePdf.ts";
import { useSlipRepair, type LegRow, type SessionRow } from "./useSlipRepair.ts";

import { ConfigureScreen } from "./components/ConfigureScreen.tsx";
import { ExcludedMarketsCard } from "./components/ExcludedMarketsCard.tsx";
import { LegCard, type LegAction } from "./components/LegCard.tsx";
import { PdfPreview } from "./components/PdfPreview.tsx";
import { RepairHero } from "./components/RepairHero.tsx";
import { RepairingScreen } from "./components/RepairingScreen.tsx";
import { SlipExecutiveSummary } from "./components/SlipExecutiveSummary.tsx";
import { SlipHealth } from "./components/SlipHealth.tsx";
import { StickyBar } from "./components/StickyBar.tsx";
import { UploadScreen, type RepairFormState } from "./components/UploadScreen.tsx";
import { ValidityStrip } from "./components/ValidityStrip.tsx";

import "./repair.css";

type ScreenState = "upload" | "configure" | "repairing" | "results";

const INITIAL_FORM: RepairFormState = {
  file: null,
  instructions: "",
  hints: [],
  entryMode: "pre-placement",
  // SR-TIERS — default to Moderate (the middle tier).
  runMode: "moderate",
  locks: [],
};

const POLL_INTERVAL_MS = 1500;

const COMING_SOON_PLACE =
  "Direct-to-bookmaker placement is in private beta. For now, copy the repaired legs into your bookmaker manually.";

// Lightweight in-page toast — replaces the native Chrome alert that
// users saw on stub features. Auto-dismisses after 4.5s, click to
// dismiss earlier. Uses inline styles so it survives without CSS.
function showToast(message: string): void {
  if (typeof document === "undefined") return;
  const root = document.body;
  const existing = root.querySelector("[data-sr-toast='1']");
  if (existing) existing.remove();
  const el = document.createElement("div");
  el.setAttribute("data-sr-toast", "1");
  el.setAttribute("role", "status");
  el.style.cssText = [
    "position:fixed",
    "top:24px",
    "right:24px",
    "z-index:9999",
    "max-width:360px",
    "padding:14px 18px",
    "background:#161410",
    "color:#f5f3ee",
    "border:1px solid rgba(212,168,67,0.55)",
    "border-radius:8px",
    "box-shadow:0 12px 32px rgba(0,0,0,0.28)",
    "font-family:Inter,-apple-system,Segoe UI,sans-serif",
    "font-size:13px",
    "line-height:1.5",
    "letter-spacing:0.005em",
    "cursor:pointer",
    "opacity:0",
    "transform:translateY(-8px)",
    "transition:opacity 200ms ease,transform 200ms ease",
  ].join(";");
  el.textContent = message;
  root.appendChild(el);
  requestAnimationFrame(() => {
    el.style.opacity = "1";
    el.style.transform = "translateY(0)";
  });
  const dismiss = (): void => {
    el.style.opacity = "0";
    el.style.transform = "translateY(-8px)";
    setTimeout(() => el.remove(), 220);
  };
  el.addEventListener("click", dismiss);
  setTimeout(dismiss, 4500);
}

// ─── LegRow → stub Leg adapter ─────────────────────────────────
// The UI components (LegCard, RepairHero, etc.) were authored against
// the stub `Leg` shape from `_stubData.ts`. Rather than rewrite every
// component, we map live LegRow → Leg here and pass through. Backend
// emits LONG canonical codes for sharp/safer markets; we surface them
// via `displayMarket()` (defined below) which falls through to the raw
// string when the code isn't in our short map.

const SHORT_MARKET: Record<string, string> = {
  match_winner_home: "Match Result · Home",
  match_winner_draw: "Match Result · Draw",
  match_winner_away: "Match Result · Away",
  double_chance_home_draw: "Double Chance · 1X",
  double_chance_home_away: "Double Chance · 12",
  double_chance_draw_away: "Double Chance · X2",
  both_teams_score_yes: "Both Teams to Score · Yes",
  both_teams_score_no: "Both Teams to Score · No",
  total_goals_over_2_5: "Total Goals · Over 2.5",
  total_goals_under_2_5: "Total Goals · Under 2.5",
  total_goals_over_1_5: "Total Goals · Over 1.5",
  total_goals_under_1_5: "Total Goals · Under 1.5",
  total_goals_over_3_5: "Total Goals · Over 3.5",
  total_goals_under_3_5: "Total Goals · Under 3.5",
  // Substrate short-form codes
  ou_over_0_5: "Total Goals · Over 0.5",
  ou_over_1_5: "Total Goals · Over 1.5",
  ou_over_2_5: "Total Goals · Over 2.5",
  ou_over_3_5: "Total Goals · Over 3.5",
  ou_over_4_5: "Total Goals · Over 4.5",
  ou_under_0_5: "Total Goals · Under 0.5",
  ou_under_1_5: "Total Goals · Under 1.5",
  ou_under_2_5: "Total Goals · Under 2.5",
  ou_under_3_5: "Total Goals · Under 3.5",
  ou_under_4_5: "Total Goals · Under 4.5",
  asian_handicap_home_minus_0_5: "Asian Handicap · Home -0.5",
  asian_handicap_home_minus_1: "Asian Handicap · Home -1",
  asian_handicap_home_minus_1_5: "Asian Handicap · Home -1.5",
  asian_handicap_home_plus_0_5: "Asian Handicap · Home +0.5",
  asian_handicap_home_plus_1: "Asian Handicap · Home +1",
  asian_handicap_home_plus_1_5: "Asian Handicap · Home +1.5",
  asian_handicap_away_minus_0_5: "Asian Handicap · Away -0.5",
  asian_handicap_away_minus_1: "Asian Handicap · Away -1",
  asian_handicap_away_minus_1_5: "Asian Handicap · Away -1.5",
  asian_handicap_away_plus_0_5: "Asian Handicap · Away +0.5",
  asian_handicap_away_plus_1: "Asian Handicap · Away +1",
  asian_handicap_away_plus_1_5: "Asian Handicap · Away +1.5",
  double_chance_home_or_draw: "Double Chance · Home or Draw",
  double_chance_home_or_away: "Double Chance · Home or Away",
  double_chance_away_or_draw: "Double Chance · Away or Draw",
};

function displayMarket(code: string | null): string {
  if (!code) return "—";
  return SHORT_MARKET[code] ?? humanizeCode(code);
}

function humanizeCode(code: string): string {
  // Generic fallback — splits snake_case into Title Case words.
  return code
    .split("_")
    .map((p) => (p.length === 0 ? p : p[0]!.toUpperCase() + p.slice(1)))
    .join(" ");
}

function buildTierPicks(row: LegRow): TierPicks {
  const tp = row.tier_picks;
  if (!tp || typeof tp !== "object") return {};
  const out: TierPicks = {};
  for (const t of ["bold", "moderate", "safer", "conservative"] as const) {
    const entry = tp[t];
    if (!entry || typeof entry !== "object") continue;
    // BUGFIX (SR-DECISION-HONESTY): previously this branch only created
    // a tier pick when market+selection+odds were all present, which
    // silently demoted UNRESOLVED / FLAG / RETAIN legs to a missing
    // pick. Downstream `currentPick = undefined` then defaulted to
    // `currentDecision = "RETAIN"`, painting UNRESOLVED legs in the UI
    // as "STRIKZA agrees with your pick" — a flat-out lie for fixtures
    // we never even matched. Always carry the decision through; only
    // require pick fields for REPLACE / MARGINAL.
    const decision = (entry.decision ?? "RETAIN") as "REPLACE" | "RETAIN" | "FLAG" | "UNRESOLVED" | "MARGINAL";
    const needsPick = decision === "REPLACE" || decision === "MARGINAL";
    if (needsPick && (!entry.market || !entry.selection || entry.odds == null)) continue;
    out[t] = {
      market: entry.market ? displayMarket(entry.market) : "",
      selection: entry.selection ? String(entry.selection) : "",
      odds: entry.odds != null ? Number(entry.odds) : 0,
      decision,
      reason: entry.reason ?? null,
    };
  }
  return out;
}

function legRowToStubLeg(row: LegRow, tier: RepairTier = "moderate"): Leg {
  // SR-TIERS: derive decision from the SELECTED tier's pick.
  const tierPicks = buildTierPicks(row);
  const currentPick = tierPicks[tier];
  const currentDecision = currentPick?.decision ?? "RETAIN";

  // Decision maps directly — no upgrade promotion needed since every
  // leg now carries a suggestion in rep_* / tier_picks.
  const decision: "retain" | "replace" | "flag" | "unresolved" =
    currentDecision === "REPLACE" || currentDecision === "MARGINAL"
      ? "replace"
      : currentDecision === "FLAG"
        ? "flag"
        : currentDecision === "UNRESOLVED"
          ? "unresolved"
          : "retain";

  // Build the `repair` shape from the current tier pick. Every leg now
  // gets a suggestion (even RETAIN), so we always build this when the
  // pick has market data.
  const saferPick = tierPicks.safer;
  const hasPick = currentPick && currentPick.market && currentPick.odds;
  const hasSafer = saferPick && saferPick.market && saferPick.odds;

  const repair = hasPick
    ? {
        sharp: {
          market: currentPick!.market ?? "—",
          selection: currentPick!.selection ?? "—",
          odds: currentPick!.odds ?? 0,
          clvDelta: 0,
          winProb: 0,
        },
        safer: {
          market: hasSafer ? (saferPick!.market ?? "—") : (currentPick!.market ?? "—"),
          selection: hasSafer ? (saferPick!.selection ?? "—") : (currentPick!.selection ?? "—"),
          odds: hasSafer ? (saferPick!.odds ?? 0) : (currentPick!.odds ?? 0),
          probDelta: 0,
          winProb: 0,
        },
      }
    : null;

  // Parse `orig_match` "Home vs Away" if present.
  const matchStr = row.orig_match ?? "";
  const m = matchStr.match(/^(.+?)\s+vs\.?\s+(.+)$/i);
  const home = m ? m[1]!.trim() : matchStr || "—";
  const away = m ? m[2]!.trim() : "";

  // SR-TIERS: prefer the per-tier reason from tier_picks; fall back to
  // the legacy sharp_reason/safer_reason columns; only then to a loading
  // placeholder.
  const reasoningBody =
    (currentPick?.reason ?? row.sharp_reason ?? row.safer_reason ?? "").trim() ||
    "Generating rationale…";

  const tierHeadline = tier === "bold"
    ? "Bold preserves the dream — kept the upside."
    : tier === "moderate"
      ? "Moderate trades upside for clarity."
      : tier === "safer"
        ? "Safer prioritises landing."
        : "Conservative protective floor.";

  const headlineForDecision =
    decision === "retain"
      ? "STRIKZA agrees with your pick."
      : decision === "replace"
        ? tierHeadline
        : decision === "flag"
          ? "Review recommended — no qualifying replacement."
          : "Coverage is partial on this fixture — you decide.";

  return {
    id: row.leg_id,
    n: row.leg_number,
    fixture: { home, away },
    league: "",
    kickoff: "",
    decision,
    confidence: row.orig_confidence ?? 0.9,
    contextSignal: "default",
    agents: { primary: "", secondary: "" }, // R1: never rendered
    repairConfidence: "MEDIUM",
    original: {
      market: row.orig_market ?? "—",
      selection: row.orig_selection ?? "—",
      odds: row.orig_odds ?? 0,
      voidRisk: (row.void_risk ?? "LOW") as VoidLevel,
      voidCondition: row.void_condition ?? undefined,
    },
    tierPicks,
    unresolvedReason:
      decision === "unresolved"
        ? {
            covered: row.fixture_id
              ? "We matched the fixture but didn't price this market this week."
              : "This fixture sits outside the leagues we priced this week.",
            missing:
              "We are not guessing in the dark — your original pick stands and we will not overwrite it.",
          }
        : undefined,
    repair,
    reasoning: {
      headline: headlineForDecision,
      body: reasoningBody,
    },
    altMode: null,
  };
}

function sessionToStubMeta(session: SessionRow, legs: LegRow[]): SlipMeta {
  const base = SLIP_META;
  // SR-TIERS — sharpOdds/saferOdds now reflect bold/safer-tier totals
  // (kept populated in lockstep on the legacy columns by the selector).
  return {
    ...base,
    bookmaker: session.bookmaker ?? base.bookmaker,
    bookmakerShort: (session.bookmaker ?? base.bookmakerShort).slice(0, 3).toUpperCase(),
    slipCode: session.session_id.slice(0, 12).toUpperCase(),
    legCount: legs.length,
    origOdds: session.total_odds_original ?? base.origOdds,
    sharpOdds: session.total_odds_bold ?? session.total_odds_sharp ?? base.sharpOdds,
    saferOdds: session.total_odds_safer_tier ?? session.total_odds_safer ?? base.saferOdds,
    voidExposure: {
      ...base.voidExposure,
      originalHigh: session.void_high_before ?? base.voidExposure.originalHigh,
      afterRepair: session.void_high_after ?? base.voidExposure.afterRepair,
    },
    executiveSummary: session.slip_summary ?? base.executiveSummary,
  };
}

// ─── Live flow ─────────────────────────────────────────────────

function LiveFlow({
  sessionId,
  setSessionId,
}: {
  sessionId: string | null;
  setSessionId: (id: string | null) => void;
}): ReactElement {
  const [screen, setScreen] = useState<ScreenState>(sessionId ? "configure" : "upload");
  const [form, setForm] = useState<RepairFormState>(INITIAL_FORM);
  // SR-TIERS — `tier` is the user-facing toggle (4-way). `mode` is the
  // legacy 2-mode mirror passed to backwards-compat APIs (patchLeg,
  // exportPdf, the legacy sharp/safer DB columns).
  const [tier, setTier] = useState<RepairTier>("moderate");
  const mode: RepairMode = tierToLegacyMode(tier);
  const setMode = (m: RepairMode): void => {
    // Map legacy 2-mode toggle hits (RepairHero) onto tiers: sharp→bold, safer→safer.
    setTier(m === "sharp" ? "bold" : "safer");
  };
  const [legActions, setLegActions] = useState<Record<string, LegAction>>({});
  const [legInflight, setLegInflight] = useState<Record<string, boolean>>({});
  const [legErrors, setLegErrors] = useState<Record<string, string | null>>({});
  const [runError, setRunError] = useState<string | null>(null);
  const [showPdf, setShowPdf] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { session, legs, loading, error, refetch } = useSlipRepair(sessionId);

  // Auto-advance: if the session is already repaired when we land on
  // configure (page refresh, bookmark, browser back), skip straight to
  // results instead of making the user click "Run repair" again.
  useEffect(() => {
    if (screen === "configure" && session?.repaired_at && !loading) {
      setScreen("results");
    }
  }, [screen, session?.repaired_at, loading]);

  // Auto-advance from "repairing" → "results" when the backend sets
  // `repaired_at`. Polling kicks in only while we're on the repairing
  // screen.
  useEffect(() => {
    if (screen !== "repairing") {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    if (session?.repaired_at) {
      setScreen("results");
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(() => {
      void refetch();
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [screen, session?.repaired_at, refetch]);

  // Poll for agent results on the results screen — the agent runs
  // async and patches tier_picks after the selector returns. Re-fetch
  // every 5s while any legs are still UNRESOLVED (agent pending).
  const agentPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (screen !== "results" || legs.length === 0) {
      if (agentPollRef.current) { clearInterval(agentPollRef.current); agentPollRef.current = null; }
      return;
    }
    const hasUnresolved = legs.some((l) => {
      const tp = l.tier_picks?.[tier] ?? null;
      return !tp || tp.decision === "UNRESOLVED";
    });
    if (!hasUnresolved) {
      if (agentPollRef.current) { clearInterval(agentPollRef.current); agentPollRef.current = null; }
      return;
    }
    if (agentPollRef.current) return;
    agentPollRef.current = setInterval(() => { void refetch(); }, 5000);
    return () => {
      if (agentPollRef.current) { clearInterval(agentPollRef.current); agentPollRef.current = null; }
    };
  }, [screen, legs, tier, refetch]);

  const onUploaded = (id: string): void => {
    setSessionId(id);
    setScreen("configure");
  };

  const onRunRepair = async (): Promise<void> => {
    if (!sessionId) return;
    setRunError(null);
    setScreen("repairing");
    try {
      // SR-TIERS — backend computes all 4 tiers regardless; we pass the
      // chosen tier as informational + set the initial render tier so the
      // results screen opens on the user's selection.
      await runRepair({ session_id: sessionId, tier: form.runMode });
      setTier(form.runMode);
      // Trigger an immediate refetch; the polling effect will catch
      // the `repaired_at` flip and advance to results.
      await refetch();
    } catch (e) {
      setRunError(e instanceof Error ? e.message : String(e));
      // Drop back to configure so the user can retry.
      setScreen("configure");
    }
  };

  const handleLegAction = async (legId: string, a: LegAction): Promise<void> => {
    if (!a || a === "keep") {
      setLegActions((prev) => ({ ...prev, [legId]: a }));
      return;
    }
    const backendAction: "ACCEPTED" | "OVERRIDDEN" | "REMOVED" =
      a === "accept" ? "ACCEPTED" : a === "override" ? "OVERRIDDEN" : "REMOVED";
    setLegInflight((p) => ({ ...p, [legId]: true }));
    setLegErrors((p) => ({ ...p, [legId]: null }));
    setLegActions((prev) => ({ ...prev, [legId]: a }));
    try {
      await patchLeg({
        leg_id: legId,
        user_action: backendAction,
        user_mode_chosen: mode,
      });
      await refetch();
    } catch (e) {
      setLegErrors((p) => ({ ...p, [legId]: e instanceof Error ? e.message : String(e) }));
    } finally {
      setLegInflight((p) => ({ ...p, [legId]: false }));
    }
  };

  const handleDownloadPdf = async (): Promise<void> => {
    if (!sessionId || !session) return;
    try {
      const payload = await exportPdf(sessionId, mode);
      // The endpoint hands us the doctrine-safe slice; reuse the
      // session+legs we already have in memory (same row, freshly
      // hydrated) so the visible state matches the PDF state.
      const builtModel = buildRepairPdfModel({
        session,
        legs,
        mode,
        tier,
        legActions,
        origin: typeof window !== "undefined" ? window.location.origin : undefined,
        referralCode: payload.referral_code,
        excludedMarkets: payload.excluded_markets,
      });
      // Attach the tier label so the cover renders "BOLD" / "MODERATE"
      // / etc. instead of the now-defunct SHARP / SAFER pair.
      const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
      const model = { ...builtModel, tierLabel };
      await downloadRepairPdf(model);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`PDF export failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handleShare = async (): Promise<void> => {
    if (!sessionId) return;
    try {
      const res = await createShareLink(sessionId);
      // Copy to clipboard if available; surface a lightweight toast.
      try {
        if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(res.share_url);
        }
      } catch {
        // clipboard write may be blocked; fall through to alert
      }
      // eslint-disable-next-line no-alert
      alert(`Share link copied · expires in 72h\n${res.share_url}`);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Share failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // ─── Render by screen ────────────────────────────────────────
  if (screen === "upload") {
    return (
      <UploadScreen
        formState={form}
        setFormState={setForm}
        onContinue={() => { /* live flow ignores the demo handle here */ }}
        onUploaded={onUploaded}
      />
    );
  }
  if (screen === "configure") {
    // Show a brief loader while the session+legs fetch is in flight,
    // otherwise ConfigureScreen would render its stub-data fallback for
    // a frame and the user would see the wrong slip.
    if (loading && legs.length === 0) {
      return (
        <div className="sr-page" style={{ padding: 24 }}>
          Loading extracted legs…
        </div>
      );
    }
    if (error) {
      return (
        <div className="sr-page" style={{ padding: 24 }}>
          <div role="alert">{error}</div>
        </div>
      );
    }
    const liveLegs: Leg[] = legs.map((l) => legRowToStubLeg(l, form.runMode));
    const liveMeta: SlipMeta | undefined = session ? sessionToStubMeta(session, legs) : undefined;
    return (
      <div>
        {runError && (
          <div
            role="alert"
            style={{
              margin: "12px 24px",
              padding: "10px 14px",
              border: "1px solid var(--neg, #d62828)",
              color: "var(--neg, #d62828)",
              borderRadius: 6,
              fontSize: 12.5,
            }}
          >
            <strong>Run failed.</strong> {runError}
          </div>
        )}
        <ConfigureScreen
          formState={form}
          setFormState={setForm}
          onRun={() => { void onRunRepair(); }}
          onBack={() => {
            setSessionId(null);
            setScreen("upload");
          }}
          legs={liveLegs}
          meta={liveMeta}
        />
      </div>
    );
  }
  if (screen === "repairing") {
    return (
      <RepairingScreen
        marketsScanned={SLIP_META.marketsScanned}
        repairedAt={session?.repaired_at ?? null}
        onDone={() => setScreen("results")}
        onTimeout={() => {
          setRunError("The repair engine timed out. Try running the repair again.");
          setScreen("configure");
        }}
      />
    );
  }

  // results
  if (loading && !session) {
    return (
      <div className="sr-page" style={{ padding: 24 }}>
        Loading repaired slip…
      </div>
    );
  }
  if (error || !session) {
    return (
      <div className="sr-page" style={{ padding: 24 }}>
        <div role="alert">{error ?? "Session not found."}</div>
      </div>
    );
  }

  const stubLegs: Leg[] = legs.map((l) => legRowToStubLeg(l, tier));
  const meta = sessionToStubMeta(session, legs);

  // SR-TIERS — base tier total from the server (pre-user-actions).
  const tierTotal: number | null =
    tier === "bold" ? session.total_odds_bold ?? session.total_odds_sharp
    : tier === "moderate" ? session.total_odds_moderate
    : tier === "safer" ? session.total_odds_safer_tier ?? session.total_odds_safer
    : session.total_odds_conservative;
  const baseFinalOdds = tierTotal ?? meta.origOdds;

  // Client-side recompute: react to user leg actions (remove / override /
  // accept / keep) so the combined odds + payout update in real time
  // without waiting for a backend refetch.
  const finalOdds = (() => {
    // If no user actions yet, trust the server total.
    const hasAnyAction = Object.values(legActions).some((a) => a !== null && a !== undefined);
    if (!hasAnyAction) return baseFinalOdds;
    let product = 1;
    let any = false;
    for (const row of legs) {
      const action = legActions[row.leg_id] ?? null;
      if (action === "remove") continue;
      const tierPick = row.tier_picks?.[tier] ?? null;
      const pickOdds = tierPick?.odds ?? null;
      const tierDecision = tierPick?.decision ?? null;
      // override/keep → original; accept on REPLACE → tier pick;
      // accept on RETAIN → tier pick (suggestion); null → depends on decision
      let odds: number | null;
      if (action === "override" || action === "keep") {
        odds = row.orig_odds;
      } else if (action === "accept" && pickOdds) {
        odds = pickOdds;
      } else if (tierDecision === "REPLACE" || tierDecision === "MARGINAL") {
        odds = pickOdds ?? row.orig_odds;
      } else {
        odds = row.orig_odds;
      }
      if (odds && odds > 0) {
        product *= odds;
        any = true;
      }
    }
    return any ? product : baseFinalOdds;
  })();

  const finalPayout = finalOdds * meta.stake;
  const finalProb =
    mode === "sharp" ? meta.sharpWinProb : meta.saferWinProb;

  const retained = session.legs_retained ?? 0;
  const replaced = session.legs_replaced ?? 0;
  const flagged = session.legs_flagged ?? 0;
  const unresolved = session.legs_unresolved ?? 0;

  return (
    <div className="sr-page" data-testid="slip-repair-results">
      <ValidityStrip
        stale={false}
        minutesLeft={meta.validUntilMin}
        repairedAt={session.repaired_at ?? meta.repairedAt}
        onRerun={() => { void onRunRepair(); }}
      />

      <SlipExecutiveSummary summary={meta.executiveSummary} />
      <SlipHealth meta={meta} mode={mode} />

      <RepairHero
        meta={meta}
        tier={tier}
        setTier={setTier}
        finalOdds={finalOdds}
        finalProb={finalProb}
        finalPayout={finalPayout}
        retained={retained}
        replaced={replaced}
        flagged={flagged}
        unresolved={unresolved}
      />

<div className="sr-section-head">
        <h2>Leg-by-leg detail</h2>
        <span className="meta">
          {stubLegs.length} legs · accept, override, or remove each replacement
        </span>
      </div>

      {stubLegs.map((leg) => (
        <LegCard
          key={leg.id}
          leg={leg}
          mode={mode}
          action={legActions[leg.id] ?? null}
          onAction={(a) => { void handleLegAction(leg.id, a); }}
          inflight={legInflight[leg.id] === true}
          actionError={legErrors[leg.id] ?? null}
        />
      ))}

      <ExcludedMarketsCard defaultOpen={false} />

      <StickyBar
        meta={meta}
        mode={mode}
        finalOdds={finalOdds}
        finalPayout={finalPayout}
        finalProb={finalProb}
        onDownload={() => { void handleDownloadPdf(); }}
        onShare={() => { void handleShare(); }}
        onReset={() => {
          setLegActions({});
        }}
        onStartNew={() => {
          setLegActions({});
          setSessionId(null);
          setScreen("upload");
        }}
        onExit={() => {
          if (typeof window !== "undefined") window.location.assign("/");
        }}
        onPlace={() => showToast(COMING_SOON_PLACE)}
      />

      {showPdf && (
        <PdfPreview
          meta={meta}
          legs={stubLegs}
          mode={mode}
          finalOdds={finalOdds}
          finalPayout={finalPayout}
          finalProb={finalProb}
          onClose={() => setShowPdf(false)}
        />
      )}
    </div>
  );
}

// ─── DEV-only stub fallback ────────────────────────────────────

function StubFlow(): ReactElement {
  const [screen, setScreen] = useState<ScreenState>("upload");
  const [form, setForm] = useState<RepairFormState>(INITIAL_FORM);
  const [tier, setTier] = useState<RepairTier>("moderate");
  const mode: RepairMode = tierToLegacyMode(tier);
  const [legActions, setLegActions] = useState<Record<string, LegAction>>({});
  const [showPdf, setShowPdf] = useState(false);
  const [stale, setStale] = useState(false);

  const setLegAction = (id: string, a: LegAction): void => {
    setLegActions((prev) => ({ ...prev, [id]: a }));
  };

  const legs: Leg[] = SLIP_LEGS;

  const { finalOdds, finalProb, retained, replaced, flagged, unresolved, afterRepairVoid } = useMemo(() => {
    let odds = 1;
    let probFactor = 1;
    let retainedCount = 0;
    let replacedCount = 0;
    let flaggedCount = 0;
    let unresolvedCount = 0;
    let voidRemaining = 0;

    for (const leg of legs) {
      const action = legActions[leg.id] ?? null;
      const dropped = action === "remove";
      if (dropped) continue;

      const useOriginal =
        action === "override" || leg.decision === "retain" || (leg.decision === "flag" && action === "keep");
      const useRepair =
        !useOriginal && leg.repair !== null && (action === "accept" || (leg.decision === "replace" && action === null));

      if (leg.decision === "retain") retainedCount += 1;
      else if (leg.decision === "replace") replacedCount += 1;
      else if (leg.decision === "flag") flaggedCount += 1;
      else if (leg.decision === "unresolved") unresolvedCount += 1;

      if (useRepair && leg.repair) {
        const pick = leg.repair[mode];
        odds *= pick.odds;
        probFactor *= pick.winProb / 100;
      } else {
        odds *= leg.original.odds;
        const p = leg.repair ? leg.repair[mode].winProb / 100 : 0.6;
        probFactor *= p;
        if (leg.original.voidRisk === "HIGH" && !useRepair) voidRemaining += 1;
      }
    }

    return {
      finalOdds: odds,
      finalProb: probFactor * 100,
      retained: retainedCount,
      replaced: replacedCount,
      flagged: flaggedCount,
      unresolved: unresolvedCount,
      afterRepairVoid: voidRemaining,
    };
  }, [legs, legActions, mode]);

  const finalPayout = finalOdds * SLIP_META.stake;

  if (screen === "upload") {
    return <UploadScreen formState={form} setFormState={setForm} onContinue={() => setScreen("configure")} />;
  }
  if (screen === "configure") {
    return (
      <ConfigureScreen
        formState={form}
        setFormState={setForm}
        onRun={() => setScreen("repairing")}
        onBack={() => setScreen("upload")}
      />
    );
  }
  if (screen === "repairing") {
    return <RepairingScreen marketsScanned={SLIP_META.marketsScanned} onDone={() => setScreen("results")} />;
  }

  return (
    <div className="sr-page" data-testid="slip-repair-results-stub">
      <ValidityStrip
        stale={stale}
        minutesLeft={SLIP_META.validUntilMin}
        repairedAt={SLIP_META.repairedAt}
        onRerun={() => {
          setStale(false);
          setScreen("repairing");
        }}
      />

      <SlipExecutiveSummary summary={SLIP_META.executiveSummary} />
      <SlipHealth meta={SLIP_META} mode={mode} />

      <RepairHero
        meta={SLIP_META}
        tier={tier}
        setTier={setTier}
        finalOdds={finalOdds}
        finalProb={finalProb}
        finalPayout={finalPayout}
        retained={retained}
        replaced={replaced}
        flagged={flagged}
        unresolved={unresolved}
      />

<div className="sr-section-head">
        <h2>Leg-by-leg detail (stub data)</h2>
        <span className="meta">{legs.length} legs</span>
      </div>

      {legs.map((leg) => (
        <LegCard
          key={leg.id}
          leg={leg}
          mode={mode}
          action={legActions[leg.id] ?? null}
          onAction={(a) => setLegAction(leg.id, a)}
        />
      ))}

      <ExcludedMarketsCard defaultOpen={false} />

      <StickyBar
        meta={SLIP_META}
        mode={mode}
        finalOdds={finalOdds}
        finalPayout={finalPayout}
        finalProb={finalProb}
        onDownload={() => setShowPdf(true)}
        onShare={() => setShowPdf(true)}
        onReset={() => setLegActions({})}
        onStartNew={() => {
          setLegActions({});
          setScreen("upload");
        }}
        onExit={() => {
          if (typeof window !== "undefined") window.location.assign("/");
        }}
        onPlace={() => { /* stub no-op */ }}
      />

      {showPdf && (
        <PdfPreview
          meta={SLIP_META}
          legs={legs}
          mode={mode}
          finalOdds={finalOdds}
          finalPayout={finalPayout}
          finalProb={finalProb}
          onClose={() => setShowPdf(false)}
        />
      )}
    </div>
  );
}

// ─── Page shell ────────────────────────────────────────────────

function SlipRepairFlow(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get("session");
  const [devStub, setDevStub] = useState(false);

  const setSessionId = (id: string | null): void => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("session", id);
    else next.delete("session");
    setSearchParams(next, { replace: false });
  };

  const isDev = typeof import.meta !== "undefined" && import.meta.env?.DEV === true;

  if (isDev && !sessionId && devStub) {
    return (
      <>
        <DevToggle on={true} onChange={setDevStub} />
        <StubFlow />
      </>
    );
  }

  return (
    <>
      {isDev && !sessionId && <DevToggle on={false} onChange={setDevStub} />}
      <LiveFlow sessionId={sessionId} setSessionId={setSessionId} />
    </>
  );
}

function DevToggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }): ReactElement {
  return (
    <div
      style={{
        margin: "8px 24px 0",
        padding: "6px 10px",
        background: "var(--bg-inset, #f5f5f5)",
        border: "1px dashed var(--border-faint, #ddd)",
        borderRadius: 4,
        fontSize: 11,
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <strong style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>
        Dev mode
      </strong>
      <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <input
          type="checkbox"
          checked={on}
          onChange={(e) => onChange(e.target.checked)}
        />
        Use stub data (no backend calls)
      </label>
    </div>
  );
}

export function SlipRepairPage(): ReactElement {
  const { hasTier, loading } = useUserTier();

  if (loading) {
    return (
      <div className="sa-root" data-testid="slip-repair-loading" style={{ padding: 24 }}>
        <div
          className="mono"
          style={{
            fontSize: 11,
            letterSpacing: "0.18em",
            color: "var(--sa-fg-muted, #666)",
            textTransform: "uppercase",
          }}
        >
          Loading…
        </div>
      </div>
    );
  }

  if (!hasTier("phantom_pro")) {
    return (
      <div className="sa-root" data-testid="slip-repair-paywall" style={{ padding: 24 }}>
        <PaywallCard
          requiredTier="phantom_pro"
          featureName="Slip Repair"
          pitch="Upload any slip and STRIKZA identifies void risk, replaces soft-line picks with sharper alternatives, and gives you a shareable PDF to act on. Available on Pro and above."
        />
      </div>
    );
  }

  return <SlipRepairFlow />;
}

export default SlipRepairPage;
